/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMealPlanDetails } from "../../redux/Slices/clientMealsTabsSlice";
import { fetchWorkoutPlanListThunk } from "../../redux/Slices/workoutPlanListSlice";
import { fetchWorkoutPlanThunk } from "../../redux/Slices/workoutPlanSlice";
import AIAssistanceMealPlanModal from "./AIAssistanceMealPlanModal";
import AIAssistanceWorkoutPlanModal from "./AIAssistanceWorkoutPlanModal";
import { fetchChatBotResponse } from "../../redux/Slices/chatBotSlice";
import AIAssistanceExistingWorkoutPlanModal from "./AIAssistanceExistingWorkoutPlanModal";

export default function AIAssistanceChat({ coachId, clientId, status }) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (coachId && clientId) {
      dispatch(fetchMealPlanDetails({ clientId }));
      dispatch(fetchWorkoutPlanListThunk({ coachId, clientId }));
    }
  }, [dispatch, clientId, coachId]);

  const { profileData } = useSelector((state) => state.profile);
  const { firstName, lastName } = useSelector(
    (state) => state.membershipOverview
  );
  const { mealPlanDetails } = useSelector((state) => state.mealTags);
  const { workoutPlanList } = useSelector(
    (state) => state?.getWorkoutPlanList ?? []
  );

  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "ai",
      isStatic: true, // 🔹 Mark as static (initial) card
      content: `Hi ${profileData?.firstName || ""
        }, This is your current meal/workout plan for ${firstName + " " + lastName
        }`,
      card: {
        type: "meal-plan",
        title: "Meal Plan",
        subtitle: `${mealPlanDetails?.recipesDetailMeal?.length || 0} Recipes`,
      },
    },
    {
      id: 2,
      type: "ai",
      isStatic: true, // 🔹 Mark as static (initial) card
      card: {
        type: "workout-plan",
        title: "Workout Plan",
        subtitle: "",
      },
    },
  ]);

  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showMealModal, setShowMealModal] = useState(false);
  const [showWorkoutModal, setShowWorkoutModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedMealPlan, setSelectedMealPlan] = useState(null);
  const [selectedWorkoutPlan, setSelectedWorkoutPlan] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [workoutModalType, setWorkoutModalType] = useState("static"); // 'static' or 'dynamic'
  const [selectedSessionsForPrompt, setSelectedSessionsForPrompt] = useState(
    []
  );
  const [selectedSessions, setSelectedSessions] = useState([]); // 🔹 Store selected session objects {id, name}
  const [recentFinalWorkoutData, setRecentFinalWorkoutData] = useState(null);
  const [selectedWorkoutResData, setSelectedWorkoutResData] = useState(null);
  const [mealModalType, setMealModalType] = useState("view");

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Update static meal plan card with API data
  useEffect(() => {
    if (mealPlanDetails) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.card?.type === "meal-plan" && msg.isStatic
            ? {
              ...msg,
              card: {
                ...msg.card,
                subtitle: `${mealPlanDetails?.recipesDetailMeal?.length || 0
                  } Recipes`,
                data: mealPlanDetails, // Store API data
              },
            }
            : msg
        )
      );
      if (!selectedMealPlan) {
        setSelectedMealPlan(mealPlanDetails);
      }
    }
  }, [mealPlanDetails]);

  // Update static workout plan card with API data
  useEffect(() => {
    if (workoutPlanList) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.card?.type === "workout-plan" && msg.isStatic
            ? {
              ...msg,
              card: {
                ...msg.card,
                subtitle:
                  workoutPlanList?.length > 0
                    ? `${workoutPlanList.length} Workout${workoutPlanList.length !== 1 ? "s" : ""
                    }`
                    : "",
                data: workoutPlanList, // Store API data
              },
            }
            : msg
        )
      );
      if (!selectedWorkoutPlan) {
        setSelectedWorkoutPlan(workoutPlanList);
      }
    }
  }, [workoutPlanList]);

  // 🔹 Handle card button click
  const handleViewClick = (cardType, cardData, isStatic = false) => {
    setModalLoading(true);

    if (cardType === "meal-plan") {
      setShowMealModal(true);

      if (isStatic) {
        setMealModalType("view");
        setIsPreviewMode(false);
        dispatch(fetchMealPlanDetails({ clientId }))
          .unwrap()
          .then((data) => {
            setSelectedMealPlan(data);
            setModalLoading(false);
          })
          .catch(() => setModalLoading(false));
      } else if (cardData?.data) {
        setMealModalType(
          cardData.title === "Meal Plan Updated"
            ? "update"
            : "view"
        );

        setIsPreviewMode(true);
        setSelectedMealPlan(cardData.data);
        setModalLoading(false);
      }

    }

    if (cardType === "workout-plan") {
      setShowWorkoutModal(true);
      // 🔹 Track modal type
      setWorkoutModalType(isStatic ? "static" : "dynamic");

      if (isStatic) {
        setIsPreviewMode(false);
        setSelectedWorkoutResData(null);
        dispatch(fetchWorkoutPlanListThunk({ coachId, clientId }))
          .unwrap()
          .then((data) => {
            setSelectedWorkoutPlan(data);
            setModalLoading(false);
          })
          .catch(() => setModalLoading(false));
      } else if (cardData?.data) {
        setIsPreviewMode(true);
        setSelectedWorkoutPlan(cardData.data);
        setSelectedWorkoutResData(cardData.resData || null);
        setModalLoading(false);
      }
    }
  };

  // Handler function
  const handleSessionsSelected = (sessions) => {
    setSelectedSessionsForPrompt(sessions);

    if (sessions.length === 0) return;

    const lastAiMessage = [...messages]
      .reverse()
      .find((m) => m.type === "ai" && !m.isStatic);

    if (!lastAiMessage?.isSessionIdSelection) return;

    const lastUserMessage = [...messages]
      .reverse()
      .find((m) => m.type === "user");

    if (lastUserMessage?.content) {
      setInputValue(lastUserMessage.content);
    }
  };

  const handleSend = (text = inputValue) => {
    if (!text.trim()) return;

    //Build the display text (with session names) and API text (with session IDs)
    let displayText = text;
    let apiText = text;

    if (selectedSessionsForPrompt.length > 0) {
      const sessionNames = selectedSessionsForPrompt
        .map((s) => s.name)
        .join(", ");
      const sessionIds = selectedSessionsForPrompt.map((s) => s.id).join(", ");

      displayText = `${text} ${sessionNames}`;
      apiText = `${text} session ids: ${sessionIds}`;
    }

    const userMessage = {
      id: messages.length + 1,
      type: "user",
      content: displayText,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);

    //Clear selected sessions and trigger modal clear
    setSelectedSessionsForPrompt([]);
    setSelectedSessions([]);
    const refreshToken = localStorage.getItem("Token");

    dispatch(
      fetchChatBotResponse({
        user_id: clientId,
        coach_id: coachId,
        query: apiText,
        token: `${refreshToken}`,
      })
    )
      .unwrap()
      .then(async (response) => {
        console.log("Full API Response:", response);
        const resData = response?.data || response?.data;
        if (!resData) {
          setMessages((prev) => [
            ...prev,
            {
              id: messages.length + 2,
              type: "ai",
              content: "Sorry, I didn't get any response. Please try again.",
              timestamp: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            },
          ]);
          return;
        }

        const aiMessages = [];
        const agentType = resData?.agent;
        const messageText =
          resData?.message || "Here's the updated information you requested.";

        console.log("test_agentType", agentType);

        //  Case 1: Meal Agent
        if (agentType === "meal_agent") {
          const updatedPlan = resData?.data?.mealPlan;

          aiMessages.push({
            id: messages.length + 2,
            type: "ai",
            isStatic: false,
            tone: "system",
            content: messageText,
            card: updatedPlan
              ? {
                type: "meal-plan",
                title: "Meal Plan Updated",
                subtitle: `${updatedPlan?.recipesDetailMeal?.length || 0
                  } Recipes`,
                data: updatedPlan,
              }
              : null,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          });
        }
        else if (agentType === "recipe_agent") {
          if (resData?.action === "clarify") {
            aiMessages.push({
              id: messages.length + 2,
              type: "ai",
              isStatic: false,
              tone: "system",
              content:
                resData?.message ||
                "Please provide more information.",
              timestamp: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            });

            setMessages((prev) => [...prev, ...aiMessages]);
            return;
          }
          if (resData?.canRefresh != null && resData?.canRefresh) {
            dispatch(fetchMealPlanDetails({ clientId }));
          }
          const isUpdated = Array.isArray(resData?.updatedMeals);

          // Only modify data for update scenarios
          const planData = isUpdated
            ? {
              ...(resData?.canRefresh != null && resData?.canRefresh) ? resData?.mealPlanDetails : mealPlanDetails,
              recipesDetailMeal: resData.updatedMeals,
            }
            : mealPlanDetails;

          const cardTitle = isUpdated
            ? "Meal Plan Updated"
            : "Current Meal Plan";

          const cardMessage =
            ((resData?.canRefresh != null && resData?.canRefresh)
              ? resData?.message?.trim() + "\n Preview the changes and click Update to save them."
              : resData?.message?.trim()) ||
            (isUpdated
              ? "Your meal plan has been modified. Preview the changes and click Update to save them."
              : "Here's your current meal plan.");

          aiMessages.push({
            id: messages.length + 2,
            type: "ai",
            isStatic: false,
            tone: "system",
            content: cardMessage,
            card: {
              type: "meal-plan",
              title: cardTitle,
              subtitle: `${planData?.recipesDetailMeal?.length || 0
                } Recipes`,
              data: planData,
            },
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          });
        }

        // 🧠 Case 2: Workout Agent → Handle modified_sessions
        else if (agentType === "workout_agent") {
          const workoutsFromResponse = Array.isArray(resData?.workouts)
            ? resData.workouts
            : null;

          const modifiedSessions = Array.isArray(resData?.updated_session)
            ? resData.updated_session
            : [];

          console.log("test_updated_newly_modifiedSessions", modifiedSessions);
          console.log("test_updated_newly_modifiedSessions", resData);
          let baseWorkoutData =
            workoutsFromResponse ||
            (Array.isArray(recentFinalWorkoutData) &&
              recentFinalWorkoutData.length
              ? recentFinalWorkoutData
              : null) ||
            (Array.isArray(workoutPlanList) ? workoutPlanList : []);

          let finalWorkoutData = baseWorkoutData;

          console.log("test_baseWorkoutData", baseWorkoutData);
          //  If we have modified_sessions, apply them on top of baseWorkoutData
          if (modifiedSessions.length && Array.isArray(baseWorkoutData)) {
            if (!baseWorkoutData.length) {
              const plans = workoutPlanList?.workoutPlans || [];
              const planIds = plans.length
                ? plans.map((plan) => plan.workoutPlanId)
                : [
                    ...new Set(
                      modifiedSessions
                        .map((session) => session.clientWorkoutPlanId)
                        .filter(Boolean)
                    ),
                  ];

              const planDetails = await Promise.all(
                planIds.map((planId) =>
                  dispatch(
                    fetchWorkoutPlanThunk({
                      clientId: workoutPlanList?.clientId || clientId,
                      coachId: workoutPlanList?.coachId || coachId,
                      coachWorkoutPlanId: planId,
                    })
                  ).unwrap()
                )
              );

              baseWorkoutData = planDetails.flatMap((plan) =>
                (plan?.clientWorkoutPlanSessionResponses || []).map(
                  (session) => {
                    const sessionId = session.clientWorkoutPlanSessionId;
                    const exercises = (
                      session.clientWorkoutPlanExerciseResponses || []
                    ).map((exercise, idx) => ({
                      exerciseId: exercise.exerciseId,
                      exercise: exercise.exercise,
                      sets: exercise.sets,
                      reps: exercise.reps,
                      sec: exercise.sec || "",
                      note: exercise.note || "",
                      exerciseImage: exercise.exerciseImage,
                      raw: {
                        clientWorkoutPlanExerciseId:
                          exercise.clientWorkoutPlanExerciseId || null,
                        clientWorkoutPlanSessionId:
                          exercise.clientWorkoutPlanSessionId || sessionId,
                        exercise: exercise.exercise,
                        sets: exercise.sets,
                        reps: exercise.reps,
                        sec: exercise.sec || "",
                        exerciseImageUrl: "",
                        exerciseVideoUrl: "",
                        exerciseImage: exercise.exerciseImage,
                        exerciseVideo: null,
                        note: exercise.note || "",
                        dropSet: "",
                        setType: "",
                        resetTimer: null,
                        exerciseId: exercise.exerciseId,
                        noOfSetsCompleted: null,
                        indexOrder: exercise.indexOrder ?? idx,
                        instructions: exercise.instructions || "",
                      },
                    }));

                    return {
                      clientWorkoutPlanId: plan.clientWorkoutPlanId,
                      name: plan.name,
                      created_on: plan.createdOn || plan.created_on,
                      session_id: sessionId,
                      session_name: session.sessionName,
                      workout_type: session.sessionType,
                      total_sets: session.totalSets,
                      exercises,
                    };
                  }
                )
              );
            }
            finalWorkoutData = baseWorkoutData.map((workout) => {
              const modified = modifiedSessions.find(
                (mod) => mod.session_id === workout.session_id
              );

              if (
                !modified ||
                !modified.updated_session ||
                !Array.isArray(modified.updated_session.exercises)
              ) {
                return workout;
              }

              const updatedSession = modified.updated_session;

              const mappedExercises = updatedSession.exercises.map(
                (exercise, idx) => ({
                  exerciseId: exercise.exerciseId,
                  exercise: exercise.exercise,
                  sets: exercise.sets,
                  reps: exercise.reps,
                  sec: exercise.sec || "",
                  note: exercise.note || "",
                  exerciseImage: exercise.exerciseImage,
                  raw: {
                    clientWorkoutPlanExerciseId:
                      exercise.clientWorkoutPlanExerciseId || null,
                    clientWorkoutPlanSessionId:
                      exercise.clientWorkoutPlanSessionId ||
                      updatedSession.session_id,
                    exercise: exercise.exercise,
                    sets: exercise.sets,
                    reps: exercise.reps,
                    sec: exercise.sec || "",
                    exerciseImageUrl: "",
                    exerciseVideoUrl: "",
                    exerciseImage: exercise.exerciseImage,
                    exerciseVideo: null,
                    note: exercise.note || "",
                    dropSet: "",
                    setType: "",
                    resetTimer: null,
                    exerciseId: exercise.exerciseId,
                    noOfSetsCompleted: null,
                    indexOrder: idx,
                    instructions: exercise.instructions || "",
                  },
                })
              );

              return {
                ...workout,
                session_name:
                  updatedSession.session_name || workout.session_name,
                exercises: mappedExercises,
                total_sets: mappedExercises.reduce(
                  (sum, ex) => sum + (parseInt(ex.sets, 10) || 0),
                  0
                ),
              };
            });
          }

          // 3️⃣ Persist latest final workout data so next session response can build on it
          if (Array.isArray(finalWorkoutData) && finalWorkoutData.length) {
            setRecentFinalWorkoutData(finalWorkoutData);
          }
          const isCurrentWorkoutMessage =
            typeof messageText === "string" &&
            messageText?.toLowerCase().includes("current workouts");

          const cardTitle = isCurrentWorkoutMessage
            ? "Current workout plan"
            : "Update workout plan";
          // 4️⃣ Push AI message with card using finalWorkoutData
          aiMessages.push({
            id: messages.length + 2,
            type: "ai",
            isStatic: false,
            isSessionIdSelection:
              typeof messageText === "string" &&
              messageText.toLowerCase().includes("provide the session id"),
            tone: "system",
            content: messageText,
            resData: resData ? resData : {},
            card:
              Array.isArray(finalWorkoutData) && finalWorkoutData.length
                ? {
                  type: "workout-plan",
                  title: cardTitle,
                  subtitle: `${finalWorkoutData.length} Workout${finalWorkoutData.length !== 1 ? "s" : ""
                    }`,
                  data: finalWorkoutData,
                  resData: resData ? resData : {},
                }
                : null,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          });
        }
        else if (agentType === "preference_agent" || agentType == "profile_agent") {
          aiMessages.push({
            id: messages.length + 2,
            type: "ai",
            isStatic: false,
            tone: "system",
            content: `${resData?.message || (agentType == "preference_agent" ? "Preferences updated successfully." : "Profile updated successfully.")
              }`,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          });
        }

        // 🧠 Case 3: Generic/contextual message
        else if (resData?.concise || resData?.contextual || resData?.engaging) {
          if (resData.concise) {
            aiMessages.push({
              id: messages.length + 2,
              type: "ai",
              isStatic: false,
              tone: "concise",
              content: resData.concise,
              timestamp: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            });
          }

          if (resData.contextual) {
            aiMessages.push({
              id: messages.length + 3,
              type: "ai",
              isStatic: false,
              tone: "contextual",
              content: resData.contextual,
              timestamp: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            });
          }

          if (resData.engaging) {
            aiMessages.push({
              id: messages.length + 4,
              type: "ai",
              isStatic: false,
              tone: "engaging",
              content: resData.engaging,
              timestamp: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            });
          }
        }

        // 🧠 Case 4: Fallback message
        else {
          aiMessages.push({
            id: messages.length + 2,
            type: "ai",
            isStatic: false,
            content:
              typeof resData === "string"
                ? resData
                : "I'm here to help! Could you please rephrase or try again?",
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          });
        }

        setMessages((prev) => [...prev, ...aiMessages]);
      })
      .catch((err) => {
        console.log("Error:", err);
        setMessages((prev) => [
          ...prev,
          {
            id: messages.length + 2,
            type: "ai",
            content:
              "Sorry, I couldn't process your request right now. Please try again later.",
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          },
        ]);
      })
      .finally(() => setIsTyping(false));
  };

  // const handleQuickReply = (reply) => handleSend(reply);

  return (
    <>
      <div className="chat-container">
        <div className="chat-card">
          <div className="chat-header">
            <h3>Chatbot</h3>
          </div>

          <div className="messages-container">
            {messages.map((message) => (
              <div key={message.id} className="message">
                {message.type === "ai" ? (
                  <div className="message-ai">
                    <div className="message-avatar avatar-ai">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect
                          x="6"
                          y="6"
                          width="4"
                          height="4"
                          fill="currentColor"
                        />
                        <rect
                          x="14"
                          y="6"
                          width="4"
                          height="4"
                          fill="currentColor"
                        />
                        <rect
                          x="6"
                          y="14"
                          width="4"
                          height="4"
                          fill="currentColor"
                        />
                        <rect
                          x="14"
                          y="14"
                          width="4"
                          height="4"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                    <div className="message-content">
                      <div className="message-bubble">
                        {message.content && (
                          <p className="message-text">{message.content}</p>
                        )}
                        {message.card && (
                          <div className="message-card">
                            <div className="card-content">
                              <div className="card-icon">
                                {message.card.type === "meal-plan" ? (
                                  <svg
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4.5 6h15M4.5 12h15M4.5 18h15"
                                    />
                                  </svg>
                                )}
                              </div>
                              <div className="card-text">
                                <h5>{message.card.title}</h5>
                                <p>{message.card.subtitle}</p>
                              </div>
                            </div>
                            <div className="card-actions">
                              {(message.card.type === "meal-plan" ||
                                message.card.type === "workout-plan") && (
                                  <button
                                    className="card-action-btn"
                                    onClick={() =>
                                      handleViewClick(
                                        message.card.type,
                                        message.card,
                                        message.isStatic
                                      )
                                    }
                                  >
                                    <svg
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      width="16"
                                      height="16"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                      />
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                      />
                                    </svg>
                                    {/* 🔹 Show "View" for static, "Preview" for dynamic */}
                                    {message.isStatic ? "View" : "Preview"}
                                  </button>
                                )}
                            </div>
                          </div>
                        )}
                      </div>
                      {message.timestamp && (
                        <span className="message-timestamp">
                          {message.timestamp}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="message-user">
                    <div className="message-content">
                      <div className="message-bubble message-bubble-user">
                        <p className="message-text message-text-user">
                          {message.content}
                        </p>
                      </div>
                      <span
                        className="message-timestamp"
                        style={{ textAlign: "right", display: "block" }}
                      >
                        {message.timestamp}
                      </span>
                    </div>
                    <div className="message-avatar avatar-user">
                      {profileData?.firstName?.slice(0, 2).toUpperCase() || "U"}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {isTyping && (
              <div className="typing-indicator">
                <div className="message-avatar avatar-ai">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="6"
                      y="6"
                      width="4"
                      height="4"
                      fill="currentColor"
                    />
                    <rect
                      x="14"
                      y="6"
                      width="4"
                      height="4"
                      fill="currentColor"
                    />
                    <rect
                      x="6"
                      y="14"
                      width="4"
                      height="4"
                      fill="currentColor"
                    />
                    <rect
                      x="14"
                      y="14"
                      width="4"
                      height="4"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div className="typing-dots">
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            {/* Show selected sessions above input */}
            {selectedSessionsForPrompt.length > 0 && (
              <div
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#f0f0f0",
                  borderRadius: "8px 8px 0 0",
                  fontSize: "13px",
                  color: "#666",
                }}
              >
                <strong>Selected sessions:</strong>{" "}
                {selectedSessionsForPrompt.map((s) => s.name).join(", ")}
                <button
                  onClick={() => setSelectedSessionsForPrompt([])}
                  style={{
                    marginLeft: "8px",
                    background: "none",
                    border: "none",
                    color: "#999",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <div className="input-wrapper">
              <input
                type="text"
                className="chat-input"
                placeholder="Try: 'Show me current workout plan' or 'I don't like chicken'"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSend()}
              />
              <button
                className="send-btn"
                onClick={() => handleSend()}
                disabled={!inputValue.trim()}
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 🔹 Meal Plan Modal */}
      <AIAssistanceMealPlanModal
        showModal={showMealModal}
        setShowModal={(show) => {
          setShowMealModal(show);
          if (!show && !isPreviewMode) {
            dispatch(fetchMealPlanDetails({ clientId }));
          }
        }}
        mealPlanDetails={isPreviewMode ? selectedMealPlan : mealPlanDetails}
        modalLoading={modalLoading}
        mealModalType={mealModalType}
        onMealPlanUpdated={() => {
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now(),
              type: "ai",
              content:
                "The meal plan changes have been applied successfully.",
              timestamp: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            },
          ]);
        }}
      />

      {/* 🔹 Workout Plan Modal */}
      {workoutModalType !== "static" ? (
        <AIAssistanceWorkoutPlanModal
          showModal={showWorkoutModal}
          setShowModal={(show) => {
            setShowWorkoutModal(show);
            if (!show && !isPreviewMode) {
              dispatch(fetchWorkoutPlanListThunk({ coachId, clientId }));
            }
          }}
          workoutPlanList={
            isPreviewMode ? selectedWorkoutPlan : workoutPlanList
          }
          modalLoading={modalLoading}
          onSessionsSelected={handleSessionsSelected}
          selectedSessions={selectedSessions}
          setSelectedSessions={setSelectedSessions}
          resData={selectedWorkoutResData}
          onWorkoutPlanUpdated={() => {
            setMessages((prev) => [
              ...prev,
              {
                id: Date.now(),
                type: "ai",
                content:
                  "The workout plan changes have been applied successfully.",
                timestamp: new Date().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              },
            ]);
          }}
        />
      ) : (
        <AIAssistanceExistingWorkoutPlanModal
          showModal={showWorkoutModal}
          setShowModal={(show) => {
            setShowWorkoutModal(show);
            if (!show && !isPreviewMode) {
              dispatch(fetchWorkoutPlanListThunk({ coachId, clientId }));
            }
          }}
          workoutPlanList={
            isPreviewMode ? selectedWorkoutPlan : workoutPlanList
          }
          modalLoading={modalLoading}
        // onSessionsSelected={handleSessionsSelected}
        />
      )}
    </>
  );
}
